// 3rd Party
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Components

// Other
import { NUM_DAYS_IN_WEEK } from 'common/constants';
import {
  updateBookingWidgetSlot,
  updateSelectedAppointment,
} from 'connect/actions';
import {
  selectBookingWidgetSlot,
  selectDataMatchInsuranceId,
  selectDataUser,
} from 'connect/selectors';
import { getInNetworkStatus } from 'pages/common/helpers/common-helpers';
import { NoOffersModal } from 'pages/profile/components/BookingWidget/NoOffersModal/NoOffersModal';
import { useOfferService } from 'services/OfferService';
import { useTemporaryQueryParams } from 'utils/customHooks/use-query-params';
import Logger from 'utils/Logger';

import RecentBookings from '../../../common/components/RecentBookings/RecentBookings';
import GiftCardSvg from '../../../common/components/svgs/GiftCardSvg';
import {
  doesProviderOfferSpecialty,
  getSpecialties,
} from '../../../results/helpers/specialty-helpers';
import {
  selectDataClinic,
  selectDataClinicBookedByPastMonth,
  selectDataClinicHours,
  selectDataClinicId,
  selectDataClinicIsInstantBook,
  selectDataClinicProviders,
  selectDataClinicSlots,
} from '../../connect/selectors';
import { filterInNetworkProviders } from '../../helpers/profile-helpers';
import InstantBookView from '../InstantBookView/InstantBookView';
import RequestAppointmentView from '../RequestAppointmentView/RequestAppointmentView';

import { ANY_DENTIST_OPTION } from './constants';
import { ProviderDropdown } from './ProviderDropdown/ProviderDropdown';
import {
  BookingWidgetWrapper,
  OfferBlock,
  OfferText,
  SubTitle,
  Title,
} from './styled-components';

// Used when there is only one provider selected
const getProvider = (providers, providerIds) =>
  providerIds.length === 1 && providers.find((p) => providerIds[0] === p.id);

const getRequestAppointmentTitle = (
  isAnyDentistSelected,
  selectedSpecialty,
  providers,
  providerIds,
) => {
  const defaultTitle = 'Practice Open Hours';
  if (isAnyDentistSelected) {
    return defaultTitle;
  }

  if (selectedSpecialty) {
    return `${selectedSpecialty.nameAlt || selectedSpecialty.name} Open Hours`;
  }

  // If there is only one provider selected, show that provider's name
  const doctorName = getProvider(providers, providerIds)?.doctor?.name;
  if (doctorName) {
    return `${doctorName}'s Weekly Schedule`;
  }

  return defaultTitle;
};

// Component
const BookingWidget = ({ id }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const offerService = useOfferService();

  const user = useSelector(selectDataUser);
  const bookedByPastMonth = useSelector(selectDataClinicBookedByPastMonth);
  const clinic = useSelector(selectDataClinic);
  const clinicHours = useSelector(selectDataClinicHours);
  const clinicId = useSelector(selectDataClinicId);
  const clinicSlots = useSelector(selectDataClinicSlots);
  const clinicIsInstantBook = useSelector(selectDataClinicIsInstantBook);
  const allProviders = useSelector(selectDataClinicProviders);
  const bookingWidgetSlot = useSelector(selectBookingWidgetSlot);
  let insurancePlanId = useSelector(selectDataMatchInsuranceId);

  if ((clinic.metadata || {}).prospectLead) {
    insurancePlanId = undefined;
  }

  const urlParams = useTemporaryQueryParams([{
    paramKey: 'skipNoOffersModal',
    variableName: 'skipNoOffersModal',
  }]);

  const [isAnyDentistSelected, setIsAnyDentistSelected] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [providerIds, setProviderIds] = useState([]);

  const [hours, setHours] = useState([]);
  const [slots, setSlots] = useState([]);

  const [isNoOffersModalOpen, setIsNoOffersModalOpen] = useState(false);
  const [noOffersModalSlotId, setNoOffersModalSlotId] = useState(null);

  const inNetworkProviders = useMemo(
    () => filterInNetworkProviders(clinicHours, insurancePlanId, allProviders),
    [clinicHours, insurancePlanId, allProviders],
  );

  const specialties = useMemo(
    () => getSpecialties({ providers: inNetworkProviders }),
    [inNetworkProviders],
  );

  const shouldShowNoOfferModal = useMemo(
    () => {
      // Check the URL param to skip the no offers modal
      // This is needed if the user is coming from the booking widget in the search v1 app
      // So they don't see the modal twice
      if (urlParams.skipNoOffersModal) {
        return false;
      }

      // If there is no active offer, don't show the modal
      if (!offerService.hasActiveOffer) {
        return false;
      }

      // If the clinic doesn't allow offers, show the modal
      return !clinic.allowOffers;
    },
    [clinic.allowOffers, offerService.hasActiveOffer, urlParams.skipNoOffersModal],
  );

  const requestAppointmentTitle = useMemo(
    () =>
      getRequestAppointmentTitle(
        isAnyDentistSelected,
        selectedSpecialty,
        inNetworkProviders,
        providerIds,
      ),
    [isAnyDentistSelected, selectedSpecialty, inNetworkProviders, providerIds],
  );

  const areAllProvidersSelected = useMemo(
    () => !providerIds.length || isAnyDentistSelected,
    [providerIds, isAnyDentistSelected],
  );

  const openNoOffersModal = useCallback((slotId) => {
    setNoOffersModalSlotId(slotId);
    setIsNoOffersModalOpen(true);
  }, []);

  useEffect(() => {
    // Set visible hours to only available provider
    if (inNetworkProviders && inNetworkProviders.length === 1) {
      setProviderIds([inNetworkProviders[0].id]);
    } else if (bookingWidgetSlot && bookingWidgetSlot.providerId) {
      setProviderIds(inNetworkProviders.map((p) => p.id));
    }
  }, [inNetworkProviders, bookingWidgetSlot]);

  useEffect(() => {
    if (areAllProvidersSelected) {
      setHours(clinicHours);
      setSlots(clinicSlots);
      return;
    }

    // If specific providers are selected
    const selectedProviders = inNetworkProviders.filter((p) =>
      providerIds.includes(p.id));

    // Combine hours from all selected providers
    const selectedProvidersHours = Array.from(
      { length: NUM_DAYS_IN_WEEK },
      (_, dayIndex) => {
        const openHours = selectedProviders
          .map((p) => p.hours[dayIndex])
        // Days with start and end set to 0 are closed, and there are no other cases where start and end can be the same
          .filter((h) => h.start < h.end);

        if (!openHours?.length) {
          return { start: 0, end: 0 };
        }

        const allStartTimes = openHours.map((h) => h.start);
        const earliestStartTime = Math.min(...allStartTimes);

        const allEndTimes = openHours.map((h) => h.end);
        const latestEndTime = Math.max(...allEndTimes);

        return { start: earliestStartTime, end: latestEndTime };
      },
    );

    setHours(selectedProvidersHours);

    // Combine slots from all selected providers
    const selectedProvidersSlots = clinicSlots?.filter(
      (slot) => selectedProviders.some((p) => p.id === slot.provider.id),
    ) || [];

    setSlots(selectedProvidersSlots);
  }, [clinicHours, clinicSlots, providerIds, areAllProvidersSelected, inNetworkProviders]);

  useEffect(() => {
    // This useEffect is triggered by query params and by InstantBookView
    if (
      !clinicSlots
      || clinicSlots.length === 0
      || !bookingWidgetSlot
      || !bookingWidgetSlot.providerId
    ) {
      return;
    }

    const requestedSlots = clinicSlots.filter(
      (slot) =>
        slot.date === bookingWidgetSlot.date
        && slot.time === bookingWidgetSlot.time,
    );

    const selectedSlot = requestedSlots.find(
      (slot) => slot.provider.id === bookingWidgetSlot.providerId,
    ) || requestedSlots[0];

    handleRequestAppointment(selectedSlot?.id);
  }, [clinicHours, clinicSlots, providerIds, bookingWidgetSlot, handleRequestAppointment]);

  const handleSelectProvider = (providerOrSpecialtyId) => {
    // We're already requesting an appointment from query params, don't clear the booking widget slot
    if (!history.location.search?.includes('bookingWidgetSlotProvider')) {
      // Clear the booking widget slot when a provider is selected so we don't open checkout when changing providers
      dispatch(updateBookingWidgetSlot(null, null, null));
    }

    if (providerOrSpecialtyId === ANY_DENTIST_OPTION) {
      setProviderIds(inNetworkProviders.map((provider) => provider.id));
      setIsAnyDentistSelected(true);
      setSelectedSpecialty(null);
      return;
    }
    setIsAnyDentistSelected(false);

    const idAsNumber = parseInt(providerOrSpecialtyId, 10);

    const selectedProvider = inNetworkProviders.find(
      (provider) => provider.id === idAsNumber,
    );

    if (selectedProvider) {
      setProviderIds([selectedProvider.id]);
      setSelectedSpecialty(null);
      return;
    }

    // Not a provider, check if it's a specialty ID
    const newSelectedSpecialty = specialties.find(
      (specialty) => specialty.id === idAsNumber,
    );

    if (!newSelectedSpecialty) {
      // This shouldn't happen, but just in case
      Logger.error(`No matching specialty or provider found for id ${providerOrSpecialtyId}`);
      setProviderIds(inNetworkProviders.map((provider) => provider.id));
      setIsAnyDentistSelected(true);
    }

    const specialtyProviderIds = inNetworkProviders
      .filter((provider) => doesProviderOfferSpecialty(provider, newSelectedSpecialty))
      .map((p) => p.id);
    setProviderIds(specialtyProviderIds);
    setSelectedSpecialty(newSelectedSpecialty);
  };

  const handleRequestAppointment = useCallback((slotId, skipOfferModal = false) => {
    if (!inNetworkProviders?.length) {
      return;
    }

    if (shouldShowNoOfferModal && !skipOfferModal) {
      openNoOffersModal(slotId);
      return;
    }

    let requestedProviderId = '';
    if (slotId && clinicSlots) {
      requestedProviderId = clinicSlots.find((slot) => slot.id === slotId)
        ?.provider?.id;
    } else if (areAllProvidersSelected) {
      requestedProviderId = inNetworkProviders[0].id;
    } else {
      [requestedProviderId] = providerIds;
    }

    const selectedProvider = inNetworkProviders.find(
      (provider) => provider.id === requestedProviderId,
    );

    dispatch(
      updateSelectedAppointment(
        requestedProviderId,
        slotId,
        selectedProvider,
        areAllProvidersSelected,
      ),
    );

    history.push(`/results/${clinicId}/checkout`);
  }, [
    dispatch,
    clinicId,
    clinicSlots,
    history,
    inNetworkProviders,
    providerIds,
    areAllProvidersSelected,
  ]);

  const handleNoOffersModalContinue = useCallback(() => {
    handleRequestAppointment(noOffersModalSlotId, true);
  }, [handleRequestAppointment, noOffersModalSlotId]);

  const showOfferBlock = useMemo(
    () => offerService.hasActiveOffer && clinic.allowOffers,
    [offerService.hasActiveOffer, clinic.allowOffers],
  );

  return (
    <BookingWidgetWrapper id={id} $addTopPadding={showOfferBlock}>
      {showOfferBlock && (
        <OfferBlock>
          <GiftCardSvg width="50px" />
          <OfferText>
            {offerService.isThirdPartyOffer
              ? offerService.offerName
              : `Get a ${offerService.offerName} through Opencare`}
          </OfferText>
        </OfferBlock>
      )}

      <div>
        <Title>Request an appointment for free</Title>
        <SubTitle>No booking fees on Opencare</SubTitle>
      </div>

      {inNetworkProviders && inNetworkProviders.length > 1
        && (
          <ProviderDropdown
            handleSelect={handleSelectProvider}
            providers={inNetworkProviders}
          />
        )}

      {clinicIsInstantBook && !user.partner
        ? (
          <InstantBookView
            allSlots={slots}
            onRequestAppointment={handleRequestAppointment}
          />
        )

        : (
          <RequestAppointmentView
            hours={hours}
            onRequestAppointment={handleRequestAppointment}
            title={requestAppointmentTitle}
            disabledCta={!inNetworkProviders?.length}
          />
        )}

      <RecentBookings
        bookedByPastMonth={bookedByPastMonth}
        inNetwork={getInNetworkStatus(insurancePlanId, clinic.insurancePlans)}
      />
      {shouldShowNoOfferModal && (
        <NoOffersModal
          isOpen={isNoOffersModalOpen}
          onClose={() => setIsNoOffersModalOpen(false)}
          onContinue={handleNoOffersModalContinue}
        />
      )}
    </BookingWidgetWrapper>
  );
};

BookingWidget.propTypes = {
  id: PropTypes.string,
};
export default BookingWidget;
